import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { CampaignType, getBidRangeData, useUpdateCampaignWizard } from 'entities/campaign';
// eslint-disable-next-line no-restricted-imports
import { BidRange } from 'entities/campaign/ui';
import type { BidRangeProps } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { FormErrors } from 'shared/ui/FormErrors';
import { isAxiosResponseError } from 'shared/lib/api';
import { showDefaultErrorToast } from 'shared/lib/notifications';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import type { ServiceType } from 'entities/service';
import { AlertBox } from 'shared/ui/AlertBox';

import {
  useBid,
  useBidTiers,
  useCampaignId,
  useCampaignType,
  useCreateCampaignStoreActions,
  useServiceObject,
} from './CreateCampaignStoreHook';

type StepBidProps = {
  onBack: () => void;
  onNext: () => void;
};

const WRONG_STEP_DATA_ERROR =
  'Campaign type or service object not found. Please ensure both campaign type and service object are set.';

function getBidRangeProps(service: ServiceType): Pick<BidRangeProps, 'bidParams' | 'bidTierGroups'> {
  const bidTierGroups = service.bidTierGroups;

  if (bidTierGroups.length) {
    return {
      bidParams: null,
      bidTierGroups,
    };
  }

  return {
    bidParams: {
      leadMinimalBid: service.leadMinimalBid ?? 0,
      apptMinimalBid: service.apptMinimalBid ?? 0,
      apptMaximumBid: service.apptMaximumBid ?? 0,
      leadMaximumBid: service.leadMaximumBid ?? 0,
    },
    bidTierGroups: [],
  };
}

export function StepBid({ onNext, onBack }: StepBidProps) {
  const campaignType = useCampaignType();
  const serviceObject = useServiceObject();

  /*
  This error is impossible in the prod environment because these variables will be initialized in previous steps.
  However, these fields also have the type undefined. To avoid checking these fields later on, I immediately check them.
  */
  if (!campaignType || !serviceObject) {
    throw new Error(WRONG_STEP_DATA_ERROR);
  }

  const { updateStepData } = useCreateCampaignStoreActions();
  const updateCampaignQuery = useUpdateCampaignWizard(useCampaignId());
  const { bidParams, bidTierGroups } = getBidRangeProps(serviceObject);
  const bid = useBid();
  const bidTiers = useBidTiers();
  const methods = useForm({ defaultValues: { bid, bidTiers } });
  const bidValue = useWatch({ control: methods.control, name: 'bid' });
  const bidTiersValue = useWatch({ control: methods.control, name: 'bidTiers' }) || [];
  const disabledNextButton = bidTiersValue.length ? bidTiersValue.some((value) => !value.bid) : !bidValue;

  const isApptCampaign = campaignType === CampaignType.appointments;
  const setRate = serviceObject.setRate;

  const onSubmit = methods.handleSubmit(async (form) => {
    updateCampaignQuery.reset();
    updateStepData(form);

    try {
      await updateCampaignQuery.mutateAsync({
        payload: getBidRangeData(form),
      });
      onNext();
    } catch (e) {
      if (!isAxiosResponseError(e)) {
        showDefaultErrorToast();
      }
    }
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className="w-138">
          <div className="text-center text-xl font-medium">Set your bid</div>
          <div className="mt-2 text-center text-sm font-normal">
            Bidding works just like an auction. The higher your bid, the more likely you are to get leads. Ask your
            Partnerships Manager for more info.
          </div>
          <BidRange
            isFocused
            isWideScreen
            className="pt-8"
            campaignType={campaignType}
            bidParams={bidParams}
            bidTierGroups={bidTierGroups}
            setRate={isApptCampaign ? setRate : undefined}
          />
          {isApptCampaign ? (
            <AlertBox
              className="my-6 !items-start border-deep-blue-100 pr-[14px]"
              iconClass="text-deep-blue-500"
              iconName="info"
            >
              <div className="text-sm">
                <div className="font-medium">Call Handling: Pricing details</div>
                <div className="mt-2 text-slate-700">
                  <div>
                    Please note that the total cost in the invoice will include the price you set plus $8 for call
                    handling per lead. The set rate is guaranteed.
                  </div>
                  <div className="mt-5">For example -</div>
                  <div>
                    If the guaranteed set rate for your campaign is 20% and we generate 2 appointments, the calculation
                    will be: 2 (appointments) × 5 (set rate multiplier) × $70 (your bid) + 2 × 5 × $8 (call handling
                    cost) = $780 total.
                  </div>
                </div>
              </div>
            </AlertBox>
          ) : null}
          <div className="mt-8 flex justify-between">
            <Button
              variant="outline-second"
              type="button"
              onClick={onBack}
              {...getDataAutoTestAttributes(['button-create-wizard-back'])}
            >
              Back
            </Button>
            <Button
              disabled={disabledNextButton}
              type="submit"
              loading={updateCampaignQuery.isLoading}
              {...getDataAutoTestAttributes(['button-create-wizard-next'])}
            >
              Next
            </Button>
          </div>
        </div>
        <FormErrors error={updateCampaignQuery.error} />
      </form>
    </FormProvider>
  );
}
